import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import { CATEGORIES, CATEGORY_ORDER, catLabel } from "@/lib/categories";
import { useI18n } from "@/lib/i18n";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const empty = { category: "Fakta", content: "", snippet: "", page: "", date: "", source_document_id: "" };

export default function ItemEditor({ caseId, item, open, onOpenChange, documents, onSaved }) {
  const { t, lang } = useI18n();
  const editing = !!item;
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(item
        ? {
            category: item.category, content: item.content, snippet: item.snippet || "",
            page: item.page ?? "", date: item.date || "", source_document_id: item.source_document_id || "",
          }
        : empty);
    }
  }, [open, item]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.content.trim()) { toast.error(t("editor.emptyError")); return; }
    setBusy(true);
    const payload = {
      category: form.category,
      content: form.content,
      snippet: form.snippet,
      page: form.page === "" ? null : Number(form.page),
      date: form.date || null,
    };
    try {
      if (editing) {
        await api.patch(`/cases/${caseId}/items/${item.id}`, payload);
        toast.success(t("editor.updated"));
      } else {
        await api.post(`/cases/${caseId}/items`, {
          ...payload, source_document_id: form.source_document_id || null,
        });
        toast.success(t("editor.added"));
      }
      onSaved();
      onOpenChange(false);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#121216] border-zinc-800 text-zinc-100 w-[calc(100vw-1.5rem)] max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl font-light">
            {editing ? t("editor.editTitle") : t("editor.addTitle")}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-1">
          <div>
            <Label className="text-zinc-400 text-xs">{t("editor.category")}</Label>
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger data-testid="item-category-select" className="mt-1.5 bg-[#0a0a0c] border-zinc-800">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121216] border-zinc-800 text-zinc-100">
                {CATEGORY_ORDER.map((k) => (
                  <SelectItem key={k} value={k}>{catLabel(k, lang)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">{t("editor.content")}</Label>
            <Textarea data-testid="item-content-input" value={form.content}
              onChange={(e) => set("content", e.target.value)}
              className="mt-1.5 bg-[#0a0a0c] border-zinc-800 min-h-[80px]" />
          </div>
          <div>
            <Label className="text-zinc-400 text-xs">{t("editor.snippet")}</Label>
            <Textarea data-testid="item-snippet-input" value={form.snippet}
              onChange={(e) => set("snippet", e.target.value)}
              placeholder={t("editor.snippetPh")}
              className="mt-1.5 bg-[#0a0a0c] border-zinc-800 min-h-[60px] font-mono-ui text-sm" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-zinc-400 text-xs">{t("editor.page")}</Label>
              <Input data-testid="item-page-input" type="number" value={form.page}
                onChange={(e) => set("page", e.target.value)}
                className="mt-1.5 bg-[#0a0a0c] border-zinc-800" />
            </div>
            <div>
              <Label className="text-zinc-400 text-xs">{t("editor.date")}</Label>
              <Input data-testid="item-date-input" value={form.date}
                onChange={(e) => set("date", e.target.value)} placeholder="YYYY-MM-DD"
                className="mt-1.5 bg-[#0a0a0c] border-zinc-800" />
            </div>
          </div>
          {!editing && documents?.length > 0 && (
            <div>
              <Label className="text-zinc-400 text-xs">{t("editor.sourceDoc")}</Label>
              <Select value={form.source_document_id} onValueChange={(v) => set("source_document_id", v)}>
                <SelectTrigger className="mt-1.5 bg-[#0a0a0c] border-zinc-800">
                  <SelectValue placeholder={t("editor.pickDoc")} />
                </SelectTrigger>
                <SelectContent className="bg-[#121216] border-zinc-800 text-zinc-100">
                  {documents.map((d) => (
                    <SelectItem key={d.id} value={d.id}>{d.filename}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button data-testid="save-item-btn" onClick={save} disabled={busy}
            className="bg-zinc-50 text-zinc-950 hover:bg-white">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : t("common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
